import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Box, EmptyState, RowsSkeletonLoader } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useGetLedgerWalletTransactions } from 'hooks/queries/Ledger/useGetLedgerWalletTransactions'
import { useCustomerEarningsSummaryQuery } from 'hooks/queries/Rewards/useCustomerEarningsSummaryQuery'
import { useRewardsPortfoliosQuery } from 'hooks/queries/Rewards/useRewardsPortfoliosQuery'
import { useRewardsProductsQuery } from 'hooks/queries/Rewards/useRewardsProductsQuery'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { RewardsEmbeddedTabs } from './components/embedded/RewardsEmbeddedTabs'
import { RewardCompoundedCalculator } from './components/RewardCompoundedCalculator'
import { RewardsAssets } from './components/RewardsAssets'
import { RewardsDashboard } from './components/RewardsDashboard'
import { RewardsNoWallets } from './components/RewardsNoWallets'
import { RewardsPageHeader } from './components/RewardsPageHeader'
import { RewardsSheetProvider } from './context/RewardsSheetProvider'
import { RewardsEmbeddedTabsEnum, useRewardsEmbeddedTabs } from './hooks/useRewardsEmbeddedTabs'
import useRewardsTracking from './hooks/useRewardsTracking'
import { useRewardsWallets } from './hooks/useRewardsWallets'
import { useTransactionsPagination } from './hooks/useTransactionsPagination'

export const Rewards = () => {
  const { t } = useTranslation()
  const { trackPageView } = useRewardsTracking()
  const { enableEmbeddedRewards } = useFeatureFlags()
  const { accountCapabilities } = useAccountsContext()
  const {
    isEmbeddedCustomerCompanyCapabilityEnabled,
    isEmbeddedCustomerIndividualCapabilityEnabled,
  } = accountCapabilities || {}
  const isEmbeddedPartner =
    isEmbeddedCustomerCompanyCapabilityEnabled || isEmbeddedCustomerIndividualCapabilityEnabled

  const isCustomerRewards = !!enableEmbeddedRewards && isEmbeddedPartner

  const { activeTab, handleChangeTab } = useRewardsEmbeddedTabs()
  const isCustomerTab = activeTab === RewardsEmbeddedTabsEnum.CUSTOMER_REWARDS

  useEffect(() => {
    trackPageView('home')
  }, [])

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useTransactionsPagination()

  const { data: transactionsData, isLoading: isLoadingTransactions } =
    useGetLedgerWalletTransactions({
      categories: ['EARNING'],
      page,
      size: rowsPerPage,
    })

  const transactions = transactionsData?.content ?? []

  const { paging } = usePaginationPagingPersist(transactionsData?.pagination)
  const showPagination = transactions.length > 0 && !isLoadingTransactions

  const {
    data: rewardsProductsData,
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
  } = useRewardsProductsQuery()
  const rewardsProducts = rewardsProductsData?.content ?? []
  const rewardsProductCurrencies =
    rewardsProducts.map(product => product?.currency)?.filter(Boolean) ?? []
  const currency = rewardsProducts[0]?.currency ?? 'USDC'

  const {
    data: rewardsPortfolios,
    isLoading: isLoadingPortfolios,
    isError: isErrorPortfolios,
  } = useRewardsPortfoliosQuery()
  const userPortfolios = rewardsPortfolios?.content ?? []
  const hasPortfolios = userPortfolios.length > 0
  const hasAnyAmountToWithdraw = userPortfolios.some(
    portfolio => Number(portfolio.balance.available) > 0
  )

  const { data: customerEarningsSummary } = useCustomerEarningsSummaryQuery({
    currency,
    enabled: isCustomerRewards,
  })

  const { rewardsWallets, isLoadingWallets } = useRewardsWallets(rewardsProductCurrencies)
  const hasWalletsForRewards = rewardsWallets?.length > 0

  const isLoading = isLoadingProducts || isLoadingPortfolios || isLoadingWallets

  if (isLoading) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (isErrorProducts || isErrorPortfolios) {
    return (
      <EmptyState
        testId="rewards-products-error-state"
        title={t('defaultError')}
        LottiePlayer={<Lottie src="connection-error" />}
      />
    )
  }

  if (!rewardsProducts.length) {
    return (
      <EmptyState
        testId="rewards-products-empty-state"
        title={t('rewards.noProductsTitle')}
        description={t('rewards.noProductsDescription')}
        LottiePlayer={<Lottie src="no-data" />}
      />
    )
  }

  if (!hasWalletsForRewards) {
    return (
      <RewardsSheetProvider
        userPortfolios={userPortfolios}
        rewardsProducts={rewardsProducts}
        isCustomerRewards={isCustomerRewards}
        customerEarningsSummary={customerEarningsSummary}
        activeTab={activeTab}
      >
        <RewardsPageHeader
          rewardsProducts={rewardsProducts}
          showWithdrawButton={false}
          showDepositButton={false}
        />
        <RewardsEmbeddedTabs
          currency={currency}
          showEmbeddedTabs={isCustomerRewards}
          activeTab={activeTab}
          onChangeTab={handleChangeTab}
        >
          <RewardsNoWallets rewardsProducts={rewardsProducts} />
        </RewardsEmbeddedTabs>
      </RewardsSheetProvider>
    )
  }

  if (!hasPortfolios) {
    return (
      <RewardsSheetProvider
        userPortfolios={userPortfolios}
        rewardsProducts={rewardsProducts}
        isCustomerRewards={isCustomerRewards}
        customerEarningsSummary={customerEarningsSummary}
        activeTab={activeTab}
      >
        <RewardsPageHeader
          rewardsProducts={rewardsProducts}
          showWithdrawButton={false}
          showDepositButton={!isCustomerTab}
        />
        <RewardsEmbeddedTabs
          currency={currency}
          showEmbeddedTabs={isCustomerRewards}
          activeTab={activeTab}
          onChangeTab={handleChangeTab}
        >
          <Box flexCol gap={16}>
            <RewardsAssets title={t('rewards.allocateToStartEarning')} />
            <RewardCompoundedCalculator />
          </Box>
        </RewardsEmbeddedTabs>
      </RewardsSheetProvider>
    )
  }

  return (
    <RewardsSheetProvider
      userPortfolios={userPortfolios}
      rewardsProducts={rewardsProducts}
      isCustomerRewards={isCustomerRewards}
      customerEarningsSummary={customerEarningsSummary}
      activeTab={activeTab}
    >
      <RewardsPageHeader
        rewardsProducts={rewardsProducts}
        showWithdrawButton={hasAnyAmountToWithdraw}
        showDepositButton={!isCustomerTab}
      />

      <RewardsEmbeddedTabs
        currency={currency}
        showEmbeddedTabs={isCustomerRewards}
        activeTab={activeTab}
        onChangeTab={handleChangeTab}
      >
        <RewardsDashboard
          transactions={transactions}
          isLoadingTransactions={isLoadingTransactions}
          showPagination={showPagination}
          page={page}
          rowsPerPage={rowsPerPage}
          paging={paging}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </RewardsEmbeddedTabs>
    </RewardsSheetProvider>
  )
}
